import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { UI_COLORS, colorHelpers } from '../../utils/colors';
import deliveryOrderService from '../../services/deliveryOrderService';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faSave, faSpinner } from '@fortawesome/free-solid-svg-icons';
import validateUtils from '../../utils/validate';
import { useTranslation } from 'react-i18next';

const todayIst = () =>
  new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' }); // YYYY-MM-DD in IST

const todayIso = todayIst; // keep existing usages working

const EditDeliveryChallanModal = ({ isOpen, onClose, subOrderId, onConfirm, loading = false }) => {
  const { t } = useTranslation();
  const [orderData, setOrderData] = useState(null);
  const [fetching, setFetching] = useState(false);
  const [error, setError] = useState(null);

  const [challanDate, setChallanDate] = useState(todayIso());
  const [vehicleNumber, setVehicleNumber] = useState('');
  const [driverContact, setDriverContact] = useState('');
  const [emptyTruckWeight, setEmptyTruckWeight] = useState('');
  const [finalTruckWeight, setFinalTruckWeight] = useState('');
  const [loadingCostPerKg, setLoadingCostPerKg] = useState('');
  const [freight, setFreight] = useState('');
  const [freightType, setFreightType] = useState('ex_warehouse');
  const [freightPricingType, setFreightPricingType] = useState('fixed');
  const [expenses, setExpenses] = useState('');
  const [expenseRemark, setExpenseRemark] = useState('');
  const [discount, setDiscount] = useState('');
  const [discountRemark, setDiscountRemark] = useState('');
  const [roundingOff, setRoundingOff] = useState('');
  const [remark, setRemark] = useState('');
  const [vehicleRemark, setVehicleRemark] = useState('');
  const [itemsUpdate, setItemsUpdate] = useState([]);
  const [errors, setErrors] = useState({});

  const strings = t('deliveryChallanModule.editModal') || {};
  const buttons = strings.buttons || {};
  const lastFetchedSubOrderId = useRef(null);

  const fetchOrderDetails = useCallback(async () => {
    setFetching(true);
    setError(null);
    try {
      const res = await deliveryOrderService.getDeliverySubOrderById(subOrderId, { force: true });
      if (res.success) {
        const data = res.data;
        setOrderData(data);
        setChallanDate(data.challan_date || todayIso());
        setVehicleNumber(data.vehicle_number || '');
        setDriverContact(data.driver_contact || '');
        setEmptyTruckWeight(data.empty_truck_weight || '');
        setFinalTruckWeight(data.final_truck_weight || '');
        setLoadingCostPerKg(data.loading_cost_per_kg || '');
        setFreight(data.freight || '');
        setFreightType(data.freight_type || 'ex_warehouse');
        setFreightPricingType(data.freight_pricing_type || 'fixed');
        setExpenses(data.expenses || '');
        setExpenseRemark(data.expense_remark || '');
        setDiscount(data.discount || '');
        setDiscountRemark(data.discount_remark || '');
        setRoundingOff(data.rounding_off || '');
        setRemark(data.remark || '');
        setVehicleRemark(data.vehicle_remark || '');
        if (data.items) {
          setItemsUpdate(data.items.map(it => ({
            id: it.id,
            product_name: it.product_name,
            size_name: it.size_name,
            unit: it.unit,
            actual_quantity: it.actual_quantity || 0,
            actual_weight: it.actual_weight || 0,
            actual_pieces: it.actual_pieces || it.quantity_in_pieces || 0,
            per_piece_weight: it.per_piece_weight || it.standard_weight || 0,
            price: it.price || 0,
            remark: it.remark || ''
          })));
        }
      } else {
        setError(res.error || 'Failed to fetch order details');
      }
    } catch (err) {
      setError(err.message || 'Failed to fetch order details');
    } finally {
      setFetching(false);
    }
  }, [subOrderId]);

  useEffect(() => {
    if (isOpen && subOrderId && lastFetchedSubOrderId.current !== subOrderId) {
      lastFetchedSubOrderId.current = subOrderId;
      fetchOrderDetails();
    }
    if (!isOpen) lastFetchedSubOrderId.current = null;
  }, [isOpen, subOrderId, fetchOrderDetails]);

  const handleItemFieldChange = (index, field, value) => {
    setItemsUpdate(prev => {
      const copy = [...prev];
      copy[index] = { ...copy[index], [field]: field === 'remark' ? value : (parseFloat(value) || 0) };
      return copy;
    });
  };

  // Auto-update Final Truck Weight based on items and empty weight
  useEffect(() => {
    if (!itemsUpdate.length) return;
    const totalItemWeight = itemsUpdate.reduce((sum, item) => sum + (parseFloat(item.actual_weight) || 0), 0);
    const emptyWeight = parseFloat(emptyTruckWeight) || 0;
    if (totalItemWeight > 0) {
      setFinalTruckWeight(Math.round(emptyWeight + totalItemWeight).toString());
    }
  }, [itemsUpdate, emptyTruckWeight]);

  const handleSubmit = () => {
    if (!subOrderId) return;

    const newErrors = {};
    if (vehicleNumber && !validateUtils.isValidVehicleNumber(vehicleNumber)) {
        newErrors.vehicleNumber = t('common.form.startTruck.messages.invalidVehicleNumber');
    }

    if (Object.keys(newErrors).length > 0) {
        setErrors(newErrors);
        return;
    }

    const items_payload = itemsUpdate.map(({ product_name, size_name, unit, ...rest }) => rest);
    onConfirm?.(subOrderId, {
      challan_date: challanDate,
      vehicle_number: vehicleNumber,
      driver_contact: driverContact,
      empty_truck_weight: parseFloat(emptyTruckWeight) || 0,
      final_truck_weight: parseFloat(finalTruckWeight) || 0,
      loading_cost_per_kg: parseFloat(loadingCostPerKg) || 0,
      expenses: parseFloat(expenses) || 0,
      expense_remark: expenseRemark,
      discount: parseFloat(discount) || 0,
      discount_remark: discountRemark,
      rounding_off: parseFloat(roundingOff) || 0,
      items_update: items_payload,
      remark,
      vehicle_remark: vehicleRemark,
      freight: parseFloat(freight) || 0,
      freight_type: freightType,
      freight_pricing_type: freightPricingType,
    });
  };

  const displayFields = useMemo(() => {
    if (!orderData) return [];
    const freightTypeLabels = {
      freight_chargeable: 'Freight Chargeable',
      free_delivery_freight_to_pay: 'Freight To Pay',
      ex_warehouse: 'Ex Warehouse',
      free_delivery_by_supplier: 'Free by Supplier',
      free_delivery_paid_by_company: 'Freight Paid by Company',
    };
    const fields = [];
    if (orderData.order_number) fields.push({ key: 'order_number', label: 'Order No', value: orderData.order_number });
    if (orderData.challan_number) fields.push({ key: 'challan_number', label: t('commonCols.challanNo'), value: orderData.challan_number });
    if (orderData.status) fields.push({ key: 'status', label: t('commonCols.status'), value: orderData.status.toUpperCase() });
    if (orderData.supervisor_name) fields.push({ key: 'supervisor_name', label: t('commonCols.supervisor'), value: orderData.supervisor_name });
    if (orderData.location_name) fields.push({ key: 'location_name', label: t('commonCols.location'), value: orderData.location_name });
    if (orderData.freight_type) fields.push({ key: 'freight_type', label: 'Freight Type', value: freightTypeLabels[orderData.freight_type] || orderData.freight_type });
    if (orderData.expected_delivery_date) fields.push({ key: 'exp_delivery', label: 'Exp. Delivery', value: orderData.expected_delivery_date });
    if (orderData.total_items) fields.push({ key: 'total_items', label: t('commonCols.totalItems'), value: orderData.total_items });
    return fields;
  }, [orderData]);

  const financialTotals = useMemo(() => {
    if (!orderData || !itemsUpdate.length) {
      return { effectiveSubtotal: 0, loadingCost: 0, totalWithoutGst: 0, totalGst: 0, totalWithGst: 0, gstPercentage: 18, freightVal: 0, freightType: '', expensesVal: 0, discountVal: 0 };
    }
    const gstPerc = parseFloat(orderData.items?.[0]?.gst_percentage) || 18.0;
    const effectiveSubtotal = itemsUpdate.reduce((sum, item) => sum + (parseFloat(item.price) || 0) * (parseFloat(item.actual_quantity) || 0), 0);
    const totalItemWeight = itemsUpdate.reduce((sum, item) => sum + (parseFloat(item.actual_weight) || 0), 0);
    const calcTruckWt = (parseFloat(finalTruckWeight) || 0) - (parseFloat(emptyTruckWeight) || 0);
    const loadingWeight = totalItemWeight > 0 ? totalItemWeight : (calcTruckWt > 0 ? calcTruckWt : 0);
    const loadingCostPerKgVal = parseFloat(loadingCostPerKg) || 0;
    const loadingCost = loadingWeight > 0 ? (loadingCostPerKgVal * loadingWeight) : (parseFloat(orderData.total_loading_cost) || 0);
    const freightVal = parseFloat(orderData.freight) || 0;
    const fType = orderData.freight_type || '';
    const isFreightChargeable = fType === 'freight_chargeable';
    const isFreightToPay = fType === 'free_delivery_freight_to_pay';
    const freightForTotal = isFreightChargeable ? freightVal : (isFreightToPay ? -freightVal : 0);
    const expensesVal = parseFloat(expenses) || 0;
    const discountVal = parseFloat(discount) || 0;
    const freightGst = isFreightChargeable ? (parseFloat(orderData.freight_gst_amount) || 0) : 0;
    const totalGst = effectiveSubtotal * (gstPerc / 100) + loadingCost * (gstPerc / 100) + freightGst + expensesVal * (gstPerc / 100) - discountVal * (gstPerc / 100);
    const totalWithoutGst = effectiveSubtotal + loadingCost + freightForTotal + expensesVal - discountVal;
    return { gstPercentage: gstPerc, effectiveSubtotal, loadingWeight, loadingCost, freightVal, freightType: fType, expensesVal, discountVal, totalWithoutGst, totalGst, totalWithGst: totalWithoutGst + totalGst };
  }, [orderData, itemsUpdate, emptyTruckWeight, finalTruckWeight, loadingCostPerKg, expenses, discount]);

  const autoRoundingOff = parseFloat(((Math.ceil(financialTotals.totalWithGst)) - financialTotals.totalWithGst).toFixed(2));
  const roundingOffValue = roundingOff !== '' ? parseFloat(roundingOff) || 0 : autoRoundingOff;
  const grandTotal = financialTotals.totalWithGst + roundingOffValue;

  // IST cutoff: printed challan from a previous day is frozen for everyone
  const isPreviousDayPrinted = orderData?.challan_printed &&
    orderData?.challan_date && orderData.challan_date < todayIst();

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 backdrop-blur-sm" style={{ backgroundColor: UI_COLORS.BACKGROUNDS.OVERLAY }}>
      <div className="w-full max-w-5xl rounded-2xl shadow-2xl max-h-[95vh] overflow-hidden flex flex-col" style={{ backgroundColor: UI_COLORS.BACKGROUNDS.CARD }}>

        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b shrink-0" style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}>
          <div>
            <h2 className="text-xl font-bold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>{strings.title}</h2>
            <p className="mt-0.5 text-sm" style={{ color: UI_COLORS.TEXT.SECONDARY }}>
              {strings.subtitle} | {orderData?.sub_order_number || ''}{orderData?.challan_number ? ` | ${orderData.challan_number}` : ''}
            </p>
          </div>
          <button onClick={onClose} className="p-2 rounded-xl hover:opacity-80 active:scale-95" style={{ color: UI_COLORS.TEXT.MUTED, backgroundColor: colorHelpers.withOpacity(UI_COLORS.TEXT.MUTED, 0.08) }}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 overflow-y-auto grow custom-scrollbar">
          {isPreviousDayPrinted ? (
            <div className="flex flex-col items-center justify-center py-16 gap-3">
              <div className="w-full max-w-lg text-center px-6 py-5 rounded-xl border-2 border-red-200 bg-red-50">
                <p className="text-sm font-bold text-red-700">Printed challans from previous days can&apos;t be edited.</p>
                <p className="text-xs text-red-500 mt-1">Challan date: {orderData.challan_date}</p>
              </div>
            </div>
          ) : fetching ? (
            <div className="flex flex-col items-center justify-center py-16">
              <div className="h-10 w-10 animate-spin rounded-full border-4 border-b-transparent" style={{ borderColor: colorHelpers.withOpacity(UI_COLORS.BRAND.PRIMARY, 0.5) }} />
              <p className="mt-4 text-sm font-semibold" style={{ color: UI_COLORS.TEXT.SECONDARY }}>{strings.fetching || 'Loading order details...'}</p>
            </div>
          ) : error ? (
            <div className="text-center py-16 text-red-500 font-medium bg-red-50 rounded-xl border border-red-100 px-6">{error}</div>
          ) : orderData ? (
            <div className="space-y-5">

              {/* Row 1: Customer (RO), Challan Date, Vehicle Number */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider mb-1.5" style={{ color: UI_COLORS.FORMS.LABEL }}>{t('common.formFields.labels.customer')}</label>
                  <div className="w-full px-4 py-2.5 rounded-xl border-2 text-sm font-medium truncate" style={{ backgroundColor: UI_COLORS.FORMS.INPUT.background, borderColor: UI_COLORS.FORMS.INPUT.border, color: UI_COLORS.FORMS.INPUT.text }} title={orderData.customer_name}>{orderData.customer_name}</div>
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider mb-1.5" style={{ color: UI_COLORS.FORMS.LABEL }}>{t('common.formFields.labels.challanDate')}</label>
                  <input type="date" value={challanDate} onChange={(e) => setChallanDate(e.target.value)} className="w-full px-4 py-2.5 rounded-xl border-2 focus:outline-none focus:border-blue-400 text-sm font-medium" style={{ backgroundColor: UI_COLORS.FORMS.INPUT.background, borderColor: UI_COLORS.FORMS.INPUT.border }} />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider mb-1.5" style={{ color: UI_COLORS.FORMS.LABEL }}>{t('common.formFields.labels.vehicleNumber')}</label>
                  <input 
                    type="text" 
                    value={vehicleNumber} 
                    onChange={(e) => {
                        setVehicleNumber(e.target.value.toUpperCase());
                        if (errors.vehicleNumber) setErrors(prev => ({ ...prev, vehicleNumber: null }));
                    }} 
                    className="w-full px-4 py-2.5 rounded-xl border-2 focus:outline-none focus:border-blue-400 text-sm font-medium" 
                    style={{ 
                        backgroundColor: UI_COLORS.FORMS.INPUT.background, 
                        borderColor: errors.vehicleNumber ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border 
                    }} 
                  />
                  {errors.vehicleNumber && <p className="text-[10px] mt-1 font-medium" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors.vehicleNumber}</p>}
                </div>
              </div>

              {/* Row 2: Driver Contact, Empty Truck Wt, Loaded Truck Wt */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider mb-1.5" style={{ color: UI_COLORS.FORMS.LABEL }}>{t('common.formFields.labels.driverContact')}</label>
                  <input type="text" value={driverContact} onChange={(e) => setDriverContact(e.target.value)} placeholder={t('common.formFields.placeholders.driverContactPhone')} className="w-full px-4 py-2.5 rounded-xl border-2 focus:outline-none focus:border-blue-400 text-sm font-medium" style={{ backgroundColor: UI_COLORS.FORMS.INPUT.background, borderColor: UI_COLORS.FORMS.INPUT.border }} />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider mb-1.5" style={{ color: UI_COLORS.FORMS.LABEL }}>{t('common.formFields.labels.emptyTruckWeightKg')}</label>
                  <input type="number" value={emptyTruckWeight} onChange={(e) => setEmptyTruckWeight(e.target.value)} onWheel={(e) => e.target.blur()} className="w-full px-4 py-2.5 rounded-xl border-2 focus:outline-none focus:border-blue-400 text-sm font-medium [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none" style={{ backgroundColor: UI_COLORS.FORMS.INPUT.background, borderColor: UI_COLORS.FORMS.INPUT.border }} />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider mb-1.5" style={{ color: UI_COLORS.FORMS.LABEL }}>{t('common.formFields.labels.loadedTruckWeightKg')}</label>
                  <input type="number" value={finalTruckWeight} onChange={(e) => setFinalTruckWeight(e.target.value)} onWheel={(e) => e.target.blur()} className="w-full px-4 py-2.5 rounded-xl border-2 focus:outline-none focus:border-blue-400 text-sm font-medium [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none" style={{ backgroundColor: UI_COLORS.FORMS.INPUT.background, borderColor: UI_COLORS.FORMS.INPUT.border }} />
                </div>
              </div>

              {/* Row 3: Loading Cost, Vehicle Remark */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider mb-1.5" style={{ color: UI_COLORS.FORMS.LABEL }}>{t('common.formFields.labels.loadingCostPerKg')}</label>
                  <input type="number" value={loadingCostPerKg} onChange={(e) => setLoadingCostPerKg(e.target.value)} onWheel={(e) => e.target.blur()} step="0.01" className="w-full px-4 py-2.5 rounded-xl border-2 focus:outline-none focus:border-blue-400 text-sm font-medium [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none" style={{ backgroundColor: UI_COLORS.FORMS.INPUT.background, borderColor: UI_COLORS.FORMS.INPUT.border }} />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider mb-1.5" style={{ color: UI_COLORS.FORMS.LABEL }}>{t('common.formFields.labels.vehicleRemark')}</label>
                  <input type="text" value={vehicleRemark} onChange={(e) => setVehicleRemark(e.target.value)} placeholder={t('common.formFields.placeholders.transportNotes')} className="w-full px-4 py-2.5 rounded-xl border-2 focus:outline-none focus:border-blue-400 text-sm font-medium" style={{ backgroundColor: UI_COLORS.FORMS.INPUT.background, borderColor: UI_COLORS.FORMS.INPUT.border }} />
                </div>
              </div>

              {/* Read-only order details grid */}
              {displayFields.length > 0 && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 rounded-xl border-2" style={{ backgroundColor: UI_COLORS.BACKGROUNDS.SECTION, borderColor: UI_COLORS.BORDERS.LIGHT }}>
                  {displayFields.map(field => (
                    <div key={field.key}>
                      <div className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-0.5">{field.label}</div>
                      <div className="text-sm font-medium text-gray-900 truncate" title={String(field.value)}>{String(field.value)}</div>
                    </div>
                  ))}
                </div>
              )}

              {/* Order remark — read-only, yellow */}
              {orderData.order_remark && (
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-wider mb-1" style={{ color: UI_COLORS.TEXT.MUTED }}>{t('common.formFields.labels.orderRemark')}</p>
                  <div className="px-3 py-2 rounded-lg text-xs font-medium bg-yellow-100 text-yellow-800 border border-yellow-200" style={{ whiteSpace: 'pre-wrap' }}>
                    {orderData.order_remark}
                  </div>
                </div>
              )}

              {/* Items */}
              <div>
                <h3 className="text-base font-bold mb-3 flex items-center gap-2" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
                  <span className="w-1.5 h-5 rounded-full" style={{ backgroundColor: UI_COLORS.BRAND.PRIMARY }} />
                  {t('common.formFields.labels.itemsAndActualQuantities')}
                </h3>
                <div className="space-y-3">
                  {itemsUpdate.map((item, idx) => {
                    const orig = orderData.items?.find(it => it.id === item.id) || orderData.items?.[idx] || {};
                    return (
                      <div key={item.id} className="p-4 rounded-xl border-2" style={{ backgroundColor: UI_COLORS.BACKGROUNDS.SECTION, borderColor: UI_COLORS.BORDERS.LIGHT }}>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {/* Product info — read only */}
                          <div>
                            <div className="text-sm font-bold text-gray-800">
                              {item.product_name} <span className="text-gray-500 font-medium text-xs">({item.size_name})</span>
                            </div>
                            <div className="flex flex-wrap gap-x-4 gap-y-0.5 mt-1.5">
                              <span className="text-xs text-gray-500"><span className="font-semibold">Requested:</span> {orig.quantity} {item.unit}</span>
                              <span className="text-xs text-gray-500"><span className="font-semibold">Pieces:</span> {orig.quantity_in_pieces}</span>
                              <span className="text-xs text-gray-500"><span className="font-semibold">Std Wt:</span> {orig.standard_weight} kg</span>
                              <span className="text-xs text-gray-500"><span className="font-semibold">GST:</span> {orig.gst_percentage}%</span>
                            </div>
                            {orig.remark && (
                              <div className="mt-1.5 px-2 py-1 rounded text-xs font-medium bg-yellow-100 text-yellow-800 border border-yellow-200 max-w-xs" style={{ display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden', whiteSpace: 'pre-wrap' }}>
                                {orig.remark}
                              </div>
                            )}
                          </div>

                          {/* Editable fields */}
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                            <div>
                              <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1 block">{t('common.formFields.labels.qtyWithUnit', { unit: item.unit })}</label>
                              <input type="number" value={item.actual_quantity} onChange={(e) => handleItemFieldChange(idx, 'actual_quantity', e.target.value)} onWheel={(e) => e.target.blur()} className="w-full px-3 py-2 rounded-lg border-2 text-sm font-bold focus:outline-none focus:border-blue-400 [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none" style={{ borderColor: UI_COLORS.FORMS.INPUT.border, backgroundColor: UI_COLORS.FORMS.INPUT.background }} />
                            </div>
                            <div>
                              <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1 block">{t('common.formFields.labels.weightKg')}</label>
                              <input type="number" value={item.actual_weight} onChange={(e) => handleItemFieldChange(idx, 'actual_weight', e.target.value)} onWheel={(e) => e.target.blur()} className="w-full px-3 py-2 rounded-lg border-2 text-sm font-bold focus:outline-none focus:border-blue-400 [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none" style={{ borderColor: UI_COLORS.FORMS.INPUT.border, backgroundColor: UI_COLORS.FORMS.INPUT.background }} />
                            </div>
                            <div>
                              <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1 block">{t('common.formFields.labels.pieces')}</label>
                              <input type="number" value={item.actual_pieces} onChange={(e) => handleItemFieldChange(idx, 'actual_pieces', e.target.value)} onWheel={(e) => e.target.blur()} className="w-full px-3 py-2 rounded-lg border-2 text-sm font-bold focus:outline-none focus:border-blue-400 [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none" style={{ borderColor: UI_COLORS.FORMS.INPUT.border, backgroundColor: UI_COLORS.FORMS.INPUT.background }} />
                            </div>
                            <div>
                              <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1 block">{t('common.formFields.labels.priceWithCurrency', { currency: t('common.symbols.currency') })}</label>
                              <input type="number" value={item.price} onChange={(e) => handleItemFieldChange(idx, 'price', e.target.value)} onWheel={(e) => e.target.blur()} className="w-full px-3 py-2 rounded-lg border-2 text-sm font-bold focus:outline-none focus:border-blue-400 [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none" style={{ borderColor: UI_COLORS.FORMS.INPUT.border, backgroundColor: UI_COLORS.FORMS.INPUT.background }} />
                            </div>
                          </div>
                        </div>

                        {/* Per piece weight + item remark */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3 pt-3 border-t border-gray-100">
                          <div>
                            <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1 block">{t('common.formFields.labels.perPieceWtKg')}</label>
                            <input type="number" value={item.per_piece_weight} onChange={(e) => handleItemFieldChange(idx, 'per_piece_weight', e.target.value)} onWheel={(e) => e.target.blur()} className="w-full px-3 py-2 rounded-lg border-2 text-sm font-bold focus:outline-none focus:border-blue-400 [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none" style={{ borderColor: UI_COLORS.FORMS.INPUT.border, backgroundColor: UI_COLORS.FORMS.INPUT.background }} />
                          </div>
                          <div className="md:col-span-2">
                            <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1 block">{t('common.formFields.labels.itemRemark')}</label>
                            <input type="text" value={item.remark} onChange={(e) => handleItemFieldChange(idx, 'remark', e.target.value)} placeholder={t('common.formFields.placeholders.itemRemark')} className="w-full px-3 py-2 rounded-lg border-2 text-xs font-medium focus:outline-none focus:border-blue-400" style={{ borderColor: UI_COLORS.FORMS.INPUT.border, backgroundColor: UI_COLORS.FORMS.INPUT.background, color: UI_COLORS.TEXT.PRIMARY }} />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Financial Adjustments */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5 p-5 rounded-2xl border-2 bg-gray-50 border-gray-100">
                <div className="space-y-2">
                  <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>{t('common.formFields.labels.expensesPlus')}</label>
                  <div className="relative">
                    <span className="absolute left-4 top-3 text-gray-400 text-sm">₹</span>
                    <input type="number" value={expenses} onChange={(e) => setExpenses(e.target.value)} onWheel={(e) => e.target.blur()} className="w-full pl-8 pr-4 py-2.5 rounded-xl border-2 focus:outline-none focus:border-blue-500 text-sm [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none" style={{ backgroundColor: UI_COLORS.FORMS.INPUT.background, borderColor: UI_COLORS.FORMS.INPUT.border }} placeholder="0.00" />
                  </div>
                  <input type="text" value={expenseRemark} onChange={(e) => setExpenseRemark(e.target.value)} className="w-full px-4 py-2.5 rounded-xl border-2 focus:outline-none focus:border-blue-500 text-sm" style={{ backgroundColor: UI_COLORS.FORMS.INPUT.background, borderColor: UI_COLORS.FORMS.INPUT.border }} placeholder={t('common.formFields.placeholders.expensesNote')} />
                </div>
                <div className="space-y-2">
                  <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>{t('common.formFields.labels.discountMinus')}</label>
                  <div className="relative">
                    <span className="absolute left-4 top-3 text-gray-400 text-sm">₹</span>
                    <input type="number" value={discount} onChange={(e) => setDiscount(e.target.value)} onWheel={(e) => e.target.blur()} className="w-full pl-8 pr-4 py-2.5 rounded-xl border-2 focus:outline-none focus:border-green-500 text-sm [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none" style={{ backgroundColor: UI_COLORS.FORMS.INPUT.background, borderColor: UI_COLORS.FORMS.INPUT.border }} placeholder="0.00" />
                  </div>
                  <input type="text" value={discountRemark} onChange={(e) => setDiscountRemark(e.target.value)} className="w-full px-4 py-2.5 rounded-xl border-2 focus:outline-none focus:border-green-500 text-sm" style={{ backgroundColor: UI_COLORS.FORMS.INPUT.background, borderColor: UI_COLORS.FORMS.INPUT.border }} placeholder={t('common.formFields.placeholders.discountNote')} />
                </div>
                <div className="space-y-2">
                  <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>{t('common.formFields.labels.orderRemark')}</label>
                  <textarea rows="3" value={remark} onChange={(e) => setRemark(e.target.value)} className="w-full px-4 py-2.5 rounded-xl border-2 text-sm font-medium focus:outline-none focus:border-blue-400 resize-none" style={{ backgroundColor: UI_COLORS.FORMS.INPUT.background, borderColor: UI_COLORS.FORMS.INPUT.border }} placeholder={t('common.formFields.placeholders.challanNotes')} />
                </div>
              </div>

              {/* Financial Summary */}
              <div className="flex justify-end">
                <div className="w-full md:w-1/2 bg-blue-50 p-5 rounded-2xl border border-blue-100 space-y-2">
                  <div className="flex justify-between text-sm text-gray-600">
                    <span>{t('common.formFields.labels.subtotal')}</span>
                    <span className="font-medium">₹ {financialTotals.effectiveSubtotal.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm text-gray-600">
                    <span>Loading Cost{loadingCostPerKg ? ` (${financialTotals.loadingWeight.toFixed(2)} kg @ ₹${loadingCostPerKg}/kg)` : ''}</span>
                    <span className="font-medium">₹ {financialTotals.loadingCost.toFixed(2)}</span>
                  </div>
                  {(() => {
                    const fType = orderData.freight_type || '';
                    const isChargeable = fType === 'freight_chargeable';
                    const isToPay = fType === 'free_delivery_freight_to_pay';
                    const amt = parseFloat(orderData.freight) || 0;
                    const labels = { freight_chargeable: 'Freight Chargeable', free_delivery_freight_to_pay: 'Freight To Pay', ex_warehouse: 'Ex Warehouse', free_delivery_by_supplier: 'Free by Supplier', free_delivery_paid_by_company: 'Freight Paid by Company' };
                    const color = isToPay ? 'text-green-600' : isChargeable ? 'text-gray-600' : 'text-gray-400';
                    const val = isToPay ? `- ₹ ${amt.toFixed(2)}` : `₹ ${amt.toFixed(2)}`;
                    return (
                      <div className={`flex justify-between text-sm ${color}`}>
                        <span>{labels[fType] || fType}</span>
                        <span className="font-medium">{val}</span>
                      </div>
                    );
                  })()}
                  {financialTotals.expensesVal > 0 && (
                    <div className="flex justify-between text-sm text-orange-600">
                      <span>{t('common.formFields.labels.expenses')}</span>
                      <span className="font-medium">₹ {financialTotals.expensesVal.toFixed(2)}</span>
                    </div>
                  )}
                  {financialTotals.discountVal > 0 && (
                    <div className="flex justify-between text-sm text-green-600">
                      <span>{t('common.formFields.labels.discount')}</span>
                      <span className="font-medium">- ₹ {financialTotals.discountVal.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="pt-2 border-t border-blue-200 flex justify-between text-sm font-bold text-blue-800">
                    <span>{t('common.formFields.labels.totalWithoutGst')}</span>
                    <span>₹ {financialTotals.totalWithoutGst.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm text-gray-600">
                    <span>GST @ {financialTotals.gstPercentage}%</span>
                    <span className="font-medium">₹ {financialTotals.totalGst.toFixed(2)}</span>
                  </div>
                  <div className="pt-2 border-t border-blue-300 flex justify-between text-xl font-bold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
                    <span>Total (inc. GST)</span>
                    <span>₹ {financialTotals.totalWithGst.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between items-center text-sm text-gray-600">
                    <span className="font-medium">{t('common.formFields.labels.roundingOff')}</span>
                    <div className="relative w-32">
                      <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-gray-400">₹</span>
                      <input
                        type="number"
                        value={roundingOff !== '' ? roundingOff : autoRoundingOff}
                        onChange={(e) => setRoundingOff(e.target.value)}
                        onWheel={(e) => e.target.blur()}
                        className="w-full pl-5 pr-2 py-1.5 rounded-lg border-2 text-sm font-bold text-gray-800 focus:outline-none focus:border-blue-400 text-right [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                        style={{ backgroundColor: UI_COLORS.FORMS.INPUT.background, borderColor: roundingOff !== '' ? '#3b82f6' : UI_COLORS.FORMS.INPUT.border }}
                        step="0.01"
                      />
                    </div>
                  </div>
                  <div className="pt-3 border-t-2 border-blue-400 flex justify-between text-2xl font-extrabold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
                    <span>Grand Total</span>
                    <span>₹ {Math.round(grandTotal)}</span>
                  </div>
                </div>
              </div>

            </div>
          ) : null}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-4 p-5 border-t shrink-0" style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}>
          <button onClick={onClose} disabled={loading} className="px-6 py-3 rounded-xl font-medium text-sm hover:opacity-80 active:scale-95" style={{ color: UI_COLORS.TEXT.PRIMARY, backgroundColor: colorHelpers.withOpacity(UI_COLORS.TEXT.MUTED, 0.12) }}>
            {buttons.cancel}
          </button>
          <button onClick={handleSubmit} disabled={loading || fetching || !!error || !orderData} className="px-8 py-3 rounded-xl font-bold text-sm flex items-center gap-2 shadow-lg hover:shadow-xl transition-shadow disabled:opacity-70 active:scale-95" style={{ backgroundColor: UI_COLORS.BRAND.PRIMARY, color: '#FFFFFF' }}>
            {loading ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faSave} />}
            <span>{loading ? buttons.saving : buttons.save}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditDeliveryChallanModal;
